import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { logger } from '../utils/logger';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const modalStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
  border: '1px solid rgba(212, 175, 55, 0.4)',
  borderRadius: '16px',
  padding: '32px',
  maxWidth: '420px',
  width: '90%',
  textAlign: 'center' as const,
  boxShadow: '0 8px 32px rgba(212, 175, 55, 0.15)',
};

const iconStyle: React.CSSProperties = {
  fontSize: '48px',
  marginBottom: '16px',
};

const titleStyle: React.CSSProperties = {
  color: '#d4af37',
  fontSize: '20px',
  fontWeight: 'bold',
  marginBottom: '12px',
};

const textStyle: React.CSSProperties = {
  color: '#b0b0c0',
  fontSize: '14px',
  lineHeight: '1.5',
  marginBottom: '24px',
};

const btnEnable: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f0d060 100%)',
  border: 'none',
  padding: '12px 32px',
  borderRadius: '10px',
  color: '#1a1a2e',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  marginBottom: '12px',
  width: '100%',
};

const btnLater: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(212, 175, 55, 0.3)',
  padding: '10px 24px',
  borderRadius: '10px',
  color: '#b0b0c0',
  fontSize: '14px',
  cursor: 'pointer',
  width: '100%',
};

interface NotificationPromptProps {
  onDismiss?: () => void;
}

const NotificationPrompt: React.FC<NotificationPromptProps> = ({ onDismiss }) => {
  const { isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show for authenticated users, when permission is 'default', and not dismissed
    if (!isAuthenticated) return;

    if (!('Notification' in window)) return;

    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed) return;

    if (Notification.permission === 'default') {
      setVisible(true);
    }
  }, [isAuthenticated]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
          ),
        });

        await api.post('/api/push/subscribe', subscription);
        logger.info('Push subscription created');
        setVisible(false);
      } else if (permission === 'denied') {
        localStorage.setItem('notification_prompt_dismissed', '1');
        setVisible(false);
      }
    } catch (error) {
      logger.error({ err: error }, 'Failed to create push subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleLater = () => {
    localStorage.setItem('notification_prompt_dismissed', '1');
    setVisible(false);
    onDismiss?.();
  };

  if (!visible) return null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={iconStyle}>🔔</div>
        <div style={titleStyle}>Включить уведомления?</div>
        <div style={textStyle}>
          Получайте уведомления о готовности прогнозов, новых анализах и важных астрономических событиях.
        </div>
        <button
          style={{ ...btnEnable, opacity: loading ? 0.6 : 1 }}
          onClick={handleEnable}
          disabled={loading}
        >
          {loading ? 'Подключение...' : 'Включить уведомления'}
        </button>
        <button style={btnLater} onClick={handleLater}>
          Позже
        </button>
      </div>
    </div>
  );
};

// Helper: convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationPrompt;
